/**
 * Training Recommendation Engine
 * 
 * V2 heuristic-based recommendation logic with adaptive update rules.
 * Pure functions for computing recommendations.
 * 
 * GUARDRAILS ENFORCED:
 * - Max 3 total recommendations
 * - Max 1 per topic cluster
 * - Max 1 per provider (unless manager override)
 * - Max 1 paid (unless manager override)
 * - Books suppressed during work hours (0.5x), normal off-shift
 * - No provider-specific boosts in code (all providers equal)
 */

// DTC-J: route the recommendation-engine cutoff/future-offering
// filters and "days until soonest" ranking factor through the canonical
// clock so scoring is deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import type {
  TrainingArtifact,
  TechnicianLearningProfile,
  TechnicianTrainingWeight,
  LearningHistoryEntry,
  LearningStyle
} from '@/types/training';
// Batch 18-D2: the recommendation caps (DEFAULT_CAPS,
// applyRecommendationCaps, diversity bias) were moved into a shared
// guardrail module. Behavior is preserved bit-for-bit. The function
// is re-exported below so the engine's public API stays compatible
// with `useSmartRecommendations.ts` and the engine test file, both of
// which import from `@/utils/trainingRecommendationEngine`.
import { applyRecommendationCaps } from './training/guardrails/caps';
export { applyRecommendationCaps };
// Batch 18-D4: TOPIC_CLUSTERS + getTopicCluster were extracted into the
// shared scoring module. The classifier reads only artifact.title,
// artifact.primary_skill, and artifact.domains[].domain_key, so the
// engine's existing call site (`getTopicCluster(artifact)` in
// scoreArtifacts) continues to compile via structural typing — the
// engine's private ArtifactWithDomains shape is a superset of the
// classifier's narrow input.
import { getTopicCluster } from './training/scoring/topicCluster';
// Batch 18-D5: BOOK_TYPES + DEEP_DIVE_TYPES + getBookTimeModifier were
// extracted into the shared scoring module. The helper reads only
// `artifact.artifact_type`, so the existing call site at scoreArtifacts
// (`getBookTimeModifier(artifact, isWorkHours)`) continues to compile
// via structural typing.
import { getBookTimeModifier } from './training/scoring/bookTime';
// Batch 18-D6: the confidence-tier multiplier table was previously
// inlined as `confidenceBoosts` inside scoreArtifacts. The values are
// now shared with `contextualTrainingScorer.ts`. The engine continues
// to apply them ADDITIVELY via `factors.confidenceBoost` (multiplicative
// vs additive is the intentional difference between the two scorers and
// is NOT unified by this batch).
import { CONFIDENCE_MULTIPLIERS } from './training/scoring/confidenceTier';
// Batch 18-D8: the engine-side explanation helpers (generateWhyShowing
// and the legacy generateExplanation) moved into the shared
// explanations module. The internal call site in scoreArtifacts uses
// the imported `generateWhyShowing` below; `generateExplanation` is
// re-exported to preserve the engine's public API even though it
// currently has no live consumer.
import { generateWhyShowing } from './training/explanations/engineExplanation';
export { generateExplanation } from './training/explanations/engineExplanation';

// =============================================
// TYPES
// =============================================

export interface TrainingProvider {
  id: string;
  name: string;
  display_name: string;
  trust_score: number;
  // Phase 4 extensions
  visibility_level?: string; // 'internal_only' | 'admin_only' | 'technician_visible'
  hidden_from_recommendations?: boolean;
  lifecycle_status?: string;
  learning_enabled?: boolean;
}

interface ArtifactWithDomains extends TrainingArtifact {
  provider?: TrainingProvider | null;
  domains?: Array<{
    domain_key: string;
    is_primary: boolean;
    relevance_score: number;
  }>;
  // Phase 2: Upcoming offerings for this artifact
  upcomingOfferings?: Array<{
    id: string;
    start_datetime: string;
    status: string;
    registration_url: string | null;
  }>;
}

export interface ScoredArtifact {
  artifact: ArtifactWithDomains;
  score: number;
  explanation: string[];
  whyShowing: string; // Human-readable "Why you're seeing this"
  factors: {
    domainRelevance: number;
    confidenceBoost: number;
    styleMatch: number;
    levelMatch: number;
    adaptiveWeight: number;
    recencyPenalty: number;
    accessBonus: number;
    bookTimeModifier: number;
    providerTrust: number;
    trainingEffectWeight: number; // Phase 3: Learning Impact Loop (±0.30 max)
    offeringBonus: number; // Phase 2: Upcoming offering bonus (capped at 0.15)
  };
  topicCluster?: string;
  nextOffering?: { date: string; registrationUrl: string } | null; // Phase 2
}

interface RecommendationInput {
  artifacts: ArtifactWithDomains[];
  profile?: TechnicianLearningProfile | null;
  weights?: TechnicianTrainingWeight[];
  history?: LearningHistoryEntry[];
  targetDomains?: string[];
  recentDays?: number;
  isWorkHours?: boolean;
  managerOverride?: boolean;
  // Phase 4: Viewer context for visibility filtering
  viewerRole?: 'technician' | 'manager' | 'admin';
  // Phase 3: Effect signals for scoring
  effectSignals?: Record<string, number>;
}

// =============================================
// CONSTANTS
// =============================================

// Topic clusters for grouping similar content
// Batch 18-D4: TOPIC_CLUSTERS + getTopicCluster moved to
// `./training/scoring/topicCluster.ts`. getTopicCluster is imported
// at the top of this file; the call site in scoreArtifacts is unchanged.

// Book artifact types for time-based suppression
// Batch 18-D5: BOOK_TYPES + DEEP_DIVE_TYPES + getBookTimeModifier moved
// to `./training/scoring/bookTime.ts`. getBookTimeModifier is imported
// at the top of this file; the call site in scoreArtifacts is unchanged.

// =============================================
// HELPER FUNCTIONS
// =============================================
// Batch 18-D8: generateWhyShowing + generateExplanation moved to
// `./training/explanations/engineExplanation.ts`. The helpers are
// imported above; the internal call site in scoreArtifacts is
// unchanged.

// =============================================
// MAIN SCORING FUNCTION
// =============================================

/**
 * Score and rank artifacts based on multiple factors
 * Phase 4: Includes visibility filtering based on viewer role
 */
export function scoreArtifacts(input: RecommendationInput): ScoredArtifact[] {
  const {
    artifacts,
    profile,
    weights = [],
    history = [],
    targetDomains = [],
    recentDays = 30,
    isWorkHours = true,
    viewerRole = 'technician', // Phase 4: Default to technician
    effectSignals = {}, // Phase 3: Effect signals for scoring
  } = input;

  // Phase 4: Filter artifacts based on provider visibility
  const visibleArtifacts = artifacts.filter(artifact => {
    const provider = artifact.provider;
    if (!provider) return true; // No provider = visible to all
    
    // Check if provider is hidden from recommendations
    if (provider.hidden_from_recommendations) return false;
    
    // Check lifecycle status
    if (provider.lifecycle_status && ['hidden', 'takedown_requested', 'disabled'].includes(provider.lifecycle_status)) {
      return false;
    }
    
    // Check visibility level based on viewer role
    const visibilityLevel = provider.visibility_level || 'technician_visible';
    
    if (visibilityLevel === 'internal_only') {
      return viewerRole === 'admin';
    }
    if (visibilityLevel === 'admin_only') {
      return viewerRole === 'admin' || viewerRole === 'manager';
    }
    
    return true; // technician_visible = visible to all
  });

  // Build recently clicked set
  const cutoffDate = clock.now();
  cutoffDate.setDate(cutoffDate.getDate() - recentDays);
  
  const recentlyClicked = new Set(
    history
      .filter(h => new Date(h.clicked_at) > cutoffDate)
      .map(h => h.artifact_id)
  );

  // Build weight lookup
  const weightLookup = new Map<string, number>();
  for (const w of weights) {
    const key = `${w.weight_type}:${w.weight_key}:${w.domain_key}`;
    weightLookup.set(key, w.weight_value);
  }

  const getWeight = (type: string, key: string, domain: string): number => {
    return weightLookup.get(`${type}:${key}:${domain}`) || 1.0;
  };

  return visibleArtifacts.map(artifact => {
    const explanation: string[] = [];
    const factors = {
      domainRelevance: 0,
      confidenceBoost: 1.0,
      styleMatch: 0,
      levelMatch: 0,
      adaptiveWeight: 1.0,
      recencyPenalty: 1.0,
      accessBonus: 0,
      bookTimeModifier: 1.0,
      providerTrust: 1.0,
      trainingEffectWeight: 0, // Phase 3: Learning Impact Loop (capped at ±0.3)
      offeringBonus: 0, // Phase 2: Upcoming offering bonus (capped at 0.15)
    };
    
    // Track next offering for UI display
    let nextOffering: { date: string; registrationUrl: string } | null = null;

    // 1. Domain relevance
    const domains = artifact.domains || [];
    let bestDomainScore = 0;
    let matchedDomain = '';
    
    for (const d of domains) {
      if (targetDomains.includes(d.domain_key)) {
        const relevance = (d.relevance_score / 100) * (d.is_primary ? 2.0 : 1.5);
        if (relevance > bestDomainScore) {
          bestDomainScore = relevance;
          matchedDomain = d.domain_key;
        }
      }
    }
    
    factors.domainRelevance = bestDomainScore;
    if (matchedDomain) {
      explanation.push(`Domain match: ${matchedDomain}`);
    }

    // 2. Confidence level boost
    // Batch 18-D6: values come from the shared confidence-tier module.
    // The `|| 1.0` fallback is preserved for runtime behavior identity
    // (defensive against any value outside the declared ConfidenceLevel
    // union; with the narrow type the lookup is total).
    factors.confidenceBoost = CONFIDENCE_MULTIPLIERS[artifact.confidence_level] || 1.0;
    if (artifact.confidence_level === 'optibay_verified') {
      explanation.push('OptiBay reviewed content');
    } else if (artifact.confidence_level === 'community_validated') {
      explanation.push('Community Validated');
    }

    // 3. Provider trust score
    // FIX-R114-MED-4: enforce the file's own contract ("no provider-specific
    // boosts in code (all providers equal)"). Previously this multiplied the
    // server-supplied `provider.trust_score` into the final score on line ~481,
    // which is a hidden ranking lever a partner provider could game by simply
    // setting their trust_score higher than 1.0. Hardcoding to 1.0 keeps the
    // factor structure (so explanations and downstream code don't break) while
    // removing the actual lever. Artifact-level quality already rides through
    // confidence_level — that is the legitimate quality signal.
    factors.providerTrust = 1.0;

    // 4. Learning profile matching
    if (profile) {
      // Style matching
      const artifactStyles = artifact.teaching_styles || [];
      const preferredStyles = profile.preferred_styles || [];
      const matchingStyles = preferredStyles.filter(s => artifactStyles.includes(s));
      
      if (matchingStyles.length > 0) {
        factors.styleMatch = 0.3 * matchingStyles.length;
        explanation.push(`Preferred style: ${matchingStyles.join(', ')}`);
      }

      // Apply inferred style weights
      for (const style of artifactStyles) {
        const inferredWeight = profile.inferred_styles?.[style as LearningStyle] || 1.0;
        factors.adaptiveWeight *= inferredWeight;
      }

      // Level matching
      if (artifact.skill_depth === profile.current_level) {
        factors.levelMatch = 0.25;
        explanation.push(`Matches level: ${profile.current_level}`);
      } else if (
        (artifact.skill_depth === 'foundation' && profile.current_level === 'applied') ||
        (artifact.skill_depth === 'applied' && profile.current_level === 'advanced')
      ) {
        factors.levelMatch = 0.1; // Adjacent level bonus
      }

      // Constraint checking
      const constraints = profile.constraints || [];
      if (constraints.includes('short_sessions') && artifact.estimated_time_minutes && artifact.estimated_time_minutes > 30) {
        factors.adaptiveWeight *= 0.5;
      }
      if (constraints.includes('no_live') && artifact.delivery_format === 'live') {
        factors.adaptiveWeight *= 0.3;
      }
      if (!constraints.includes('travel_ok') && artifact.travel_required) {
        factors.adaptiveWeight *= 0.2;
      }
    }

    // 5. Apply adaptive weights from history
    for (const domain of targetDomains) {
      // Style weights
      for (const style of artifact.teaching_styles || []) {
        factors.adaptiveWeight *= getWeight('style', style, domain);
      }
      // Artifact type weight
      factors.adaptiveWeight *= getWeight('artifact_type', artifact.artifact_type, domain);
      // Provider weight (no hardcoded boosts - all equal in code)
      factors.adaptiveWeight *= getWeight('provider', artifact.provider_name, domain);
      // Scope weight
      factors.adaptiveWeight *= getWeight('scope', artifact.artifact_scope, domain);
    }

    // 6. Recency penalty
    if (recentlyClicked.has(artifact.id)) {
      factors.recencyPenalty = 0.3;
      explanation.push('Recently viewed');
    }

    // 7. Access bonus
    if (artifact.access_type === 'free') {
      factors.accessBonus = 0.15;
    }

    // 8. Book/deep-dive time modifier (GUARDRAIL: suppress books during work hours)
    factors.bookTimeModifier = getBookTimeModifier(artifact, isWorkHours);

    // 9. Phase 2: Offering bonus (capped at 0.15)
    // Only applies if offering is future, active, and has registration_url
    const offerings = artifact.upcomingOfferings || [];
    const validOfferings = offerings.filter(o => 
      o.status === 'upcoming' &&
      o.registration_url &&
      new Date(o.start_datetime) > clock.now()
    );
    
    if (validOfferings.length > 0) {
      // Find soonest offering
      const soonest = validOfferings.reduce((min, o) => 
        new Date(o.start_datetime) < new Date(min.start_datetime) ? o : min
      );
      
      const daysUntil = Math.ceil(
        (new Date(soonest.start_datetime).getTime() - clock.nowMs()) / (1000 * 60 * 60 * 24)
      );
      
      // CAPPED bonuses - max 0.15
      if (daysUntil <= 7) {
        factors.offeringBonus = 0.15;
        explanation.push('Session available this week');
      } else if (daysUntil <= 14) {
        factors.offeringBonus = 0.12;
        explanation.push('Session available soon');
      } else if (daysUntil <= 30) {
        factors.offeringBonus = 0.08;
      } else {
        factors.offeringBonus = 0.05;
      }
      
      // Track for UI display
      nextOffering = {
        date: soonest.start_datetime,
        registrationUrl: soonest.registration_url,
      };
    }

    // 10. Phase 3: Apply effect signal weight from input
    if (effectSignals && effectSignals[artifact.id]) {
      // Already capped at ±0.30 by useLearningImpactLoop
      factors.trainingEffectWeight = effectSignals[artifact.id];
      if (factors.trainingEffectWeight > 0.1) {
        // FIX-R114-MED-5: previous wording "Shown impact for others" reads as
        // "this training caused a positive outcome", which violates the
        // OptiBay philosophy that the system guides testing/learning rather
        // than implying causal repair effectiveness. Reframe to engagement
        // language only — what we actually measured is that other techs
        // engaged with this artifact in similar work contexts.
        explanation.push('Used by other techs for similar work');
      }
    }

    // Calculate topic cluster for caps enforcement
    const topicCluster = getTopicCluster(artifact);

    // Calculate final score (includes Phase 2 offeringBonus and Phase 3 trainingEffectWeight)
    const score = (
      factors.domainRelevance +
      factors.confidenceBoost +
      factors.styleMatch +
      factors.levelMatch +
      factors.accessBonus +
      factors.offeringBonus + // Phase 2: Offering bonus
      factors.trainingEffectWeight // Phase 3: Learning Impact Loop influence
    ) * factors.adaptiveWeight * factors.recencyPenalty * factors.bookTimeModifier * factors.providerTrust;

    const scoredArtifact: ScoredArtifact = {
      artifact,
      score,
      explanation,
      whyShowing: '',
      factors,
      topicCluster,
      nextOffering, // Phase 2: Include for UI display
    };

    // Generate "Why you're seeing this"
    scoredArtifact.whyShowing = generateWhyShowing(scoredArtifact);

    return scoredArtifact;
  })
  // Date-bound artifact suppression: if artifact is live/date-bound and has no future offerings, zero out score
  .map(scored => {
    const { artifact } = scored;
    const isDateBound = 
      artifact.delivery_format === 'live' ||
      ['live_seminar', 'webinar', 'hands_on_lab'].includes(artifact.artifact_type);
    
    if (isDateBound) {
      const validOfferings = (artifact.upcomingOfferings || []).filter(o =>
        o.status === 'upcoming' && new Date(o.start_datetime) > clock.now()
      );
      if (validOfferings.length === 0) {
        return { ...scored, score: 0 };
      }
    }
    return scored;
  })
  .sort((a, b) => b.score - a.score || a.artifact.id.localeCompare(b.artifact.id));
}

// =============================================
// CAPS ENFORCEMENT (GUARDRAILS)
// Batch 18-D2: the cap-enforcement code (DEFAULT_CAPS, applyDiversityBias,
// applyRecommendationCaps, RecommendationCaps) was moved into
// `./training/guardrails/caps.ts`. `applyRecommendationCaps` is
// re-exported at the top of this file for backwards-compatible imports.
// `getRecommendations` below calls the imported function.
// =============================================

/**
 * Get final recommendations with all guardrails applied
 */
export function getRecommendations(input: RecommendationInput): ScoredArtifact[] {
  const scored = scoreArtifacts(input);
  return applyRecommendationCaps(scored, {
    managerOverride: input.managerOverride,
  });
}

// =============================================
// LEGACY API (for backwards compatibility)
// =============================================
// Batch 18-D8: `generateExplanation` moved to
// `./training/explanations/engineExplanation.ts` and is re-exported
// from this file at the top of the imports section to preserve the
// engine's public API.

// =============================================
// ADAPTIVE LEARNING
// =============================================

export interface AdaptiveUpdateInput {
  eventType: 'completed' | 'abandoned' | 'rated' | 'opened';
  rating?: number; // 1-5
  confidenceDelta?: number; // -2 to +2
}

export interface WeightUpdate {
  weightType: 'style' | 'provider' | 'artifact_type' | 'scope';
  weightKey: string;
  delta: number;
}

const WEIGHT_INCREMENT = 0.1;

/**
 * Calculate weight updates based on feedback
 */
export function calculateWeightUpdates(
  input: AdaptiveUpdateInput,
  artifact: TrainingArtifact
): WeightUpdate[] {
  const updates: WeightUpdate[] = [];
  
  // Determine signal direction
  const isPositive = 
    (input.eventType === 'completed' && (input.rating === undefined || input.rating >= 4)) ||
    (input.confidenceDelta !== undefined && input.confidenceDelta >= 1);
  
  const isNegative = 
    input.eventType === 'abandoned' ||
    (input.rating !== undefined && input.rating <= 2);
  
  if (!isPositive && !isNegative) {
    return updates; // Neutral feedback, no weight changes
  }
  
  const delta = isPositive ? WEIGHT_INCREMENT : -WEIGHT_INCREMENT;
  
  // Style weights
  for (const style of artifact.teaching_styles || []) {
    updates.push({
      weightType: 'style',
      weightKey: style,
      delta,
    });
  }
  
  // Artifact type weight
  updates.push({
    weightType: 'artifact_type',
    weightKey: artifact.artifact_type,
    delta,
  });
  
  // Provider weight (no special treatment - all providers equal)
  updates.push({
    weightType: 'provider',
    weightKey: artifact.provider_name,
    delta,
  });
  
  // Scope weight
  updates.push({
    weightType: 'scope',
    weightKey: artifact.artifact_scope,
    delta,
  });
  
  return updates;
}

/**
 * Apply decay to prevent overfitting
 */
export function applyWeightDecay(currentWeight: number, decayFactor = 0.98): number {
  // Decay towards 1.0 (neutral)
  return 1.0 + (currentWeight - 1.0) * decayFactor;
}

/**
 * Clamp weight to valid range
 */
export function clampWeight(weight: number, min = 0.1, max = 3.0): number {
  return Math.max(min, Math.min(max, weight));
}
